/* ==========================================================================
   MAAREE — Match Quiz engine (production)
   Self-contained, no dependencies. Reads live product data emitted by the
   Liquid section (#mq-products) and config from the root element's dataset.

   Recommends one of FIVE STYLES (colours are not part of the result):
     Solidarity · Battle · Empower · Uplift · Supportive Seamless

   Each style maps to one representative live product handle in the section
   settings so the result screen can render a real PDP image, price, and
   Add-to-bag button. The recommendation itself is purely style-based.

   Updated 2026-05-30:
   - Fixed: redirect-to-cart toggle now respected. Liquid emits "none" when
     the toggle is off; the JS treats "none" as "no redirect, stay on page".

   Updated 2026-05-31 (major fix pass):
   - Weighted scoring. Impact level is the dominant signal (weight 10),
     size next (3), strap and features lower (1 each). This stops the
     algorithm from out-voting "high impact" by stacking low-importance
     matches on a medium-impact bra. High impact answers now always
     recommend Battle or Solidarity, medium recommends Empower or Uplift,
     low recommends Supportive or Uplift.
   - "Any" / "unsure" / "mix" answers are no-ops. They no longer add a
     no-signal +1 to every bra (which was making ties pile up and break
     to Battle every time).
   - Display percentage (the "73% match" number on screen) is computed
     unweighted — it's the fraction of answered questions the winning bra
     matched on, so the user sees a number they can reason about.
   - Tiebreaker priority favours more versatile bras (Empower > Uplift >
     Supportive > Solidarity > Battle) so a user with no strong signals
     gets a sensible default instead of always seeing Battle.
   - Fixed onsite Klaviyo tracking. The old code called
     window.klaviyo.push([...]) which is a no-op: window.klaviyo is the
     modern Klaviyo API object (with .identify() and .track() methods),
     not the legacy _learnq array. klaviyoSubscribe() now detects which
     API the snippet exposes and identifies + tracks via the right one.
   - Fixed server-side Subscriptions API. The v2024-10-15 endpoint
     requires an explicit email marketing consent block on the profile;
     without it the request 400s and the catch swallows the error. Added
     the required `subscriptions.email.marketing.consent: "SUBSCRIBED"`
     so profiles actually land in the configured list. Also added a
     console.warn on non-OK responses so misconfiguration is debuggable.

   Updated 2026-06-05 (Klaviyo hand-off rebuilt — the real fix):
   - Root cause: the quiz had NEVER sent an event to Klaviyo (no
     "Completed Match Quiz" metric existed in the account). The onsite
     track() call was a silent no-op, and /client/subscriptions/ only
     subscribes to a list — it never creates the completed-quiz event,
     so the name/email + results had nowhere to land.
   - klaviyoSubscribe() now replicates the old Lantern quiz behaviour via
     three browser-safe Client API calls (public key only): create the
     profile, fire the completed-quiz event with the full result payload,
     then opt into the list. Onsite track() removed to avoid double-counting;
     onsite identify() kept for cookie linking.
   - DROP-IN: the event is fired under the SAME metric the live "Bra Quiz
     Participants" flow triggers on — "Completed Lantern Quiz (v2) -
     sports-bra-quiz" — and buildLanternPayload() reproduces Lantern's exact
     event shape (products[] with price.{amount,currency}, answers[],
     session_id, results_url_*). So the existing flow and its "Your Sports
     Bra Quiz Results" email work with zero changes in Klaviyo.
   ========================================================================== */
(function () {
  var root = document.querySelector('.mq-root[data-mq]');
  if (!root) return;

  var stage = root.querySelector('#mq-stage');
  var rawRedirect = root.dataset.cartRedirect || '';
  var cfg = {
    klaviyoKey:   root.dataset.klaviyoKey   || '',
    klaviyoList:  root.dataset.klaviyoList  || '',
    cartRedirect: (rawRedirect && rawRedirect !== 'none') ? rawRedirect : '',
    sizeGuideUrl: root.dataset.sizeGuideUrl || '',
    fittingUrl:   root.dataset.fittingUrl   || '',
    ffCss:        root.dataset.ffCss        || '',
    ffJs:         root.dataset.ffJs         || '',
    ffSizing:     root.dataset.ffSizing     || ''
  };

  /* ---- Live product data (may contain nulls for handles not found) ---- */
  var LIVE = {};
  try {
    var dataEl = document.getElementById('mq-products');
    if (dataEl) LIVE = JSON.parse(dataEl.textContent) || {};
  } catch (e) { LIVE = {}; }

  function liveOf(key) {
    var p = LIVE[key];
    return (p && p.title) ? p : null;
  }

  /* ---- Static catalogue copy (category + display name fall-backs) ----
     Prices/images/links all come from LIVE; this is only labels + categories
     used by the match copy. */
  var PRODUCTS = {
    battle:    { name: 'Battle Encapsulation', short: 'Battle',     cat: 'High-impact support' },
    solidarity:{ name: 'Solidarity',           short: 'Solidarity', cat: 'High-impact support' },
    empower:   { name: 'Empower',              short: 'Empower',    cat: 'Medium-impact support' },
    supportive:{ name: 'Supportive Seamless',  short: 'Supportive', cat: 'All-day support' },
    uplift:    { name: 'Uplift Crew Neck',     short: 'Uplift',     cat: 'Medium / all-day' }
  };
  var ALL = ['battle', 'solidarity', 'empower', 'supportive', 'uplift'];
  // Score every style. Whether a colour is in stock doesn't change the style
  // recommendation; the result page falls back to placeholder copy if LIVE
  // data for the winner is missing.
  var ELIGIBLE = ALL.slice();

  /* ---- Match maps (each map lists the styles that score for each answer) ---- */
  var M_IMPACT = {
    high:   ['battle', 'solidarity'],
    medium: ['empower', 'uplift'],
    low:    ['supportive', 'uplift'],
    mix:    []   // "I do a mix" / no signal — skipped from scoring
  };
  var M_SIZE = {
    ab:     ['uplift', 'empower', 'supportive'],
    ce:     ['empower', 'battle', 'solidarity'],
    fplus:  ['solidarity', 'battle'],
    unsure: []   // no signal — skipped
  };
  var M_STRAP = {
    racer:       ['empower', 'uplift', 'supportive'],
    hback:       ['battle', 'solidarity'],
    convertible: ['battle', 'solidarity'],
    any:         []   // no signal — skipped
  };
  var M_FEAT = {
    separation:    ['battle', 'solidarity'],
    'wide-straps': ['supportive', 'uplift'],
    adjustable:    ['solidarity'],
    'no-clasp':    ['supportive', 'uplift'],
    'step-in':     ['empower', 'uplift', 'supportive'],
    seamless:      ['supportive'],
    'high-neck':   ['uplift', 'supportive'],
    any:           []   // no signal — skipped
  };

  /* ---- Weights ----
     Impact is the dominant axis — a "high impact" answer should always end
     up recommending Battle or Solidarity, regardless of how many minor
     matches accumulate on a medium-impact bra. */
  var WEIGHTS = { impact: 10, size: 3, strap: 1, features: 1 };

  /* ---- Tie-breaker priority ----
     Used only when two or more styles tie on weighted hits. Favours the
     more versatile / lower-impact bra so a user with no strong signals
     doesn't always converge on Battle. Higher number wins. */
  var TIE_PRIORITY = {
    empower:    5,
    uplift:     4,
    supportive: 3,
    solidarity: 2,
    battle:     1
  };

  var answers = { impact: null, size: null, strap: null, features: [], name: '', email: '' };
  var flow = ['intro', 'q1', 'q2', 'q3', 'q4', 'calc', 'preview', 'result'];
  var idx = 0;

  /* ---- Scoring ----
     Two parallel tallies per style:
       hits        = weighted score (used to rank — impact dominates)
       matchCount  = unweighted match count (used for the displayed pct)
     "Any" / "unsure" / "mix" / empty answers are skipped entirely so they
     never inflate the denominator or hand out no-signal points. */
  function scoreAll(a) {
    var hits = {};
    var matchCount = {};
    ELIGIBLE.forEach(function (p) { hits[p] = 0; matchCount[p] = 0; });

    function applyMatch(set, weight) {
      (set || []).forEach(function (p) {
        if (hits[p] !== undefined) {
          hits[p] += weight;
          matchCount[p] += 1;
        }
      });
    }

    var answered = 0;

    if (a.impact && M_IMPACT[a.impact] && M_IMPACT[a.impact].length) {
      applyMatch(M_IMPACT[a.impact], WEIGHTS.impact);
      answered++;
    }
    if (a.size && M_SIZE[a.size] && M_SIZE[a.size].length) {
      applyMatch(M_SIZE[a.size], WEIGHTS.size);
      answered++;
    }
    if (a.strap && M_STRAP[a.strap] && M_STRAP[a.strap].length) {
      applyMatch(M_STRAP[a.strap], WEIGHTS.strap);
      answered++;
    }
    if (a.features && a.features.length) {
      var featProds = {};
      var realFeatures = 0;
      a.features.forEach(function (f) {
        if (!f || f === 'any') return;
        var set = M_FEAT[f];
        if (set && set.length) {
          realFeatures++;
          set.forEach(function (p) { featProds[p] = 1; });
        }
      });
      if (realFeatures > 0) {
        applyMatch(Object.keys(featProds), WEIGHTS.features);
        answered++;
      }
    }

    // Floor denominator at 1 so we never divide by zero when every answer
    // was "any" / "unsure" / "mix".
    if (!answered) answered = 1;

    var ranked = ELIGIBLE.map(function (k) {
      return {
        key: k,
        hits: hits[k],
        pct: Math.round((matchCount[k] / answered) * 100),
        name: PRODUCTS[k].short,
        cat: PRODUCTS[k].cat
      };
    }).sort(function (x, y) {
      if (y.hits !== x.hits) return y.hits - x.hits;
      return (TIE_PRIORITY[y.key] || 0) - (TIE_PRIORITY[x.key] || 0);
    });
    return { ranked: ranked, denom: answered };
  }

  function computeMatch(a) {
    var r = scoreAll(a).ranked[0];
    return r ? Math.max(r.pct, 60) : 0;
  }

  function reasonFor(p, a) {
    var bits = [];
    if (a.impact && (M_IMPACT[a.impact] || []).indexOf(p.key) > -1) bits.push(p.cat.toLowerCase());
    if (a.size === 'fplus' && (M_SIZE.fplus || []).indexOf(p.key) > -1) bits.push('F+ supportive');
    if (a.strap === 'convertible' && (M_STRAP.convertible || []).indexOf(p.key) > -1) bits.push('convertible straps');
    if (a.strap === 'hback' && (M_STRAP.hback || []).indexOf(p.key) > -1) bits.push('H-back option');
    (a.features || []).forEach(function (f) {
      if ((M_FEAT[f] || []).indexOf(p.key) > -1) {
        if (f === 'step-in') bits.push('step-in');
        if (f === 'separation') bits.push('separates breasts');
        if (f === 'seamless') bits.push('seamless');
        if (f === 'high-neck') bits.push('high neck');
        if (f === 'no-clasp') bits.push('no clasp');
        if (f === 'wide-straps') bits.push('wide straps');
        if (f === 'adjustable') bits.push('adjustable');
      }
    });
    if (!bits.length) bits.push('great all-rounder for what you described');
    var joined = bits.slice(0, 4).join(', ');
    return joined.charAt(0).toUpperCase() + joined.slice(1) + '.';
  }

  function mapResult(a) {
    var top = scoreAll(a).ranked[0];
    return {
      key: top.key,
      name: PRODUCTS[top.key].short,
      cat: PRODUCTS[top.key].cat,
      reason: reasonFor(top, a),
      live: liveOf(top.key)
    };
  }

  /* ---- Render ---- */
  function render() {
    var id = flow[idx];
    var t = document.getElementById('mq-t-' + id);
    if (!t) return;
    stage.innerHTML = '';
    stage.appendChild(t.content.cloneNode(true));
    bind();
    if (id === 'calc') runCalc();
    if (id === 'preview') runPreview();
    if (id === 'result') runResult();
  }

  function runCalc() {
    var el = stage.querySelector('#mq-calc-pct');
    var target = computeMatch(answers);
    var start = Date.now();
    var advanced = false;
    function advance() { if (advanced) return; advanced = true; idx++; render(); }
    (function step() {
      var p = Math.min(1, (Date.now() - start) / 1800);
      if (el) el.textContent = Math.round(p * target) + '%';
      if (p < 1) requestAnimationFrame(step);
      else setTimeout(advance, 350);
    })();
    // Guaranteed fallback if rAF is throttled (e.g. user tabs away mid-calc).
    setTimeout(function () { if (el) el.textContent = target + '%'; advance(); }, 2400);
  }

  function runPreview() {
    var map = mapResult(answers);
    var m = computeMatch(answers);
    var live = map.live;
    setText('#mq-prev-pct', m + '% match');
    setText('#mq-prev-match', m + '% match');
    setText('#mq-prev-cat', map.cat);
    setText('#mq-prev-name', live ? live.title : (map.name + ' Sports Bra'));
    setText('#mq-prev-price', live ? live.price : '');
    var img = stage.querySelector('#mq-prev-img');
    if (img && live && live.image) { img.style.backgroundImage = 'url("' + live.image + '")'; img.textContent = ''; }
  }

  function runResult() {
    var map = mapResult(answers);
    var m = computeMatch(answers);
    var live = map.live;
    var ranked = scoreAll(answers).ranked;

    setText('#mq-r-pct', m + '%');
    setText('#mq-r-greet', 'Our top match');
    setText('#mq-r-name', live ? live.title : (map.name + ' Sports Bra'));
    setText('#mq-r-name2', live ? live.title : (map.name + ' Sports Bra'));
    setText('#mq-r-cat', map.cat);
    setText('#mq-r-reason', map.reason);
    setText('#mq-r-price', live ? live.price : '');

    var hero = stage.querySelector('#mq-r-img');
    if (hero) {
      if (live && live.image) { hero.style.backgroundImage = 'url("' + live.image + '")'; hero.textContent = ''; }
      else hero.textContent = '[ ' + map.name + ' photo ]';
    }

    buildVariantPicker(live);
    buildOtherMatches(ranked);
  }

  /* ---- Variant picker + add to cart ---- */
  function buildVariantPicker(live) {
    var wrap = stage.querySelector('#mq-r-variants');
    var addBtn = stage.querySelector('#mq-r-add');
    if (!wrap || !addBtn) return;
    wrap.innerHTML = '';

    if (!live) {
      addBtn.disabled = true;
      addBtn.textContent = 'Currently unavailable';
      return;
    }

    var variants = (live.variants || []).filter(function (v) { return v; });
    var optionNames = live.option_names || [];
    var hasRealOptions = optionNames.length && !(optionNames.length === 1 && /title/i.test(optionNames[0]));

    var selected = []; // current option-value selection per option index

    function firstAvailableValues() {
      var v = variants.filter(function (x) { return x.available; })[0] || variants[0];
      return v ? (v.options || []).slice() : [];
    }
    selected = firstAvailableValues();

    function resolveVariant() {
      return variants.filter(function (v) {
        return (v.options || []).every(function (val, i) { return val === selected[i]; });
      })[0] || null;
    }

    function syncAdd() {
      var v = resolveVariant();
      if (v && v.available) {
        addBtn.disabled = false;
        addBtn.dataset.variantId = v.id;
        addBtn.textContent = 'Add to bag — ' + (v.price || live.price);
      } else if (v) {
        addBtn.disabled = true;
        addBtn.dataset.variantId = '';
        addBtn.textContent = 'Sold out';
      } else {
        addBtn.disabled = true;
        addBtn.dataset.variantId = '';
        addBtn.textContent = 'Select options';
      }
    }

    if (hasRealOptions) {
      optionNames.forEach(function (name, oi) {
        var values = [];
        variants.forEach(function (v) {
          var val = (v.options || [])[oi];
          if (val != null && values.indexOf(val) === -1) values.push(val);
        });
        var label = document.createElement('label');
        label.className = 'q-label';
        label.textContent = name;
        var sel = document.createElement('select');
        sel.className = 'q-input';
        values.forEach(function (val) {
          var soldOut = !variants.some(function (v) { return (v.options || [])[oi] === val && v.available; });
          var opt = document.createElement('option');
          opt.value = val;
          opt.textContent = soldOut ? (val + ' — sold out') : val;
          if (val === selected[oi]) opt.selected = true;
          sel.appendChild(opt);
        });
        sel.addEventListener('change', function () { selected[oi] = sel.value; syncAdd(); });
        wrap.appendChild(label);
        wrap.appendChild(sel);
      });
    }
    syncAdd();

    addBtn.addEventListener('click', function () {
      var vid = addBtn.dataset.variantId;
      if (!vid) return;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      addToCart(vid).then(function () {
        if (cfg.cartRedirect) window.location.href = cfg.cartRedirect;
        else { addBtn.textContent = 'Added ✓'; }
      }).catch(function () {
        addBtn.disabled = false;
        addBtn.textContent = 'Try again';
      });
    });
  }

  function addToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    }).then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); });
  }

  function buildOtherMatches(ranked) {
    var alts = ranked.slice(1, 3);
    var slots = ['#mq-r-alt1', '#mq-r-alt2'];
    slots.forEach(function (sel, i) {
      var el = stage.querySelector(sel);
      if (!el) return;
      var r = alts[i];
      if (!r) { el.style.display = 'none'; return; }
      var live = liveOf(r.key);
      var pct = Math.max(r.pct, 55);
      el.querySelector('.q-mini__match').textContent = pct + '% match';
      el.querySelector('.q-mini__name').textContent = PRODUCTS[r.key].short;
      var img = el.querySelector('.q-mini__img');
      if (live && live.image) { img.style.backgroundImage = 'url("' + live.image + '")'; img.textContent = ''; }
      else img.textContent = '[' + PRODUCTS[r.key].short + ']';
      if (live && live.url) { el.setAttribute('href', live.url); }
      else el.removeAttribute('href');
    });
  }

  /* ---- Build the Klaviyo event payload in the OLD Lantern quiz's shape ----
     The live flow "Bra Quiz Participants" triggers on the metric
     "Completed Lantern Quiz (v2) - sports-bra-quiz" and its result email
     reads event.products[] (title/url/image/price.amount/price.currency)
     and event.answers[]. We reproduce that exact structure so the existing
     flow + email work with zero changes in Klaviyo. */
  var Q_TEXT = {
    impact:   'What type of sports are you looking to use your bra for?',
    size:     'Roughly what bra size are you?',
    strap:    'What is your preferred strap design?',
    features: 'Do you have any non-negotiable features?'
  };
  var L_IMPACT = { high: 'High-impact sports', medium: 'Medium-impact sports', low: 'Low-impact / all-day', mix: 'A bit of everything' };
  var L_SIZE   = { ab: 'A–B cup', ce: 'C–E cup', fplus: 'F+ cup', unsure: "I'm not sure" };
  var L_STRAP  = { racer: 'Racerback', hback: 'H-back', convertible: 'Convertible straps', any: "I'm open to any strap design" };
  var L_FEAT   = {
    separation: 'Separates the breasts', 'wide-straps': 'Widest possible shoulder straps',
    adjustable: 'Adjustable straps', 'no-clasp': 'No clasp', 'step-in': 'Step-in design',
    seamless: 'Seamless', 'high-neck': 'High neck', any: "I'm open to anything"
  };

  function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function buildLanternPayload(a, map) {
    var ranked = scoreAll(a).ranked;

    // products[] — top match + two alternates, with live product data.
    var products = ranked.slice(0, 3).map(function (r, i) {
      var lv = liveOf(r.key);
      var amount = '';
      if (lv && lv.price_raw != null && !isNaN(Number(lv.price_raw))) {
        amount = (Number(lv.price_raw) / 100).toFixed(2);   // 4900 -> "49.00"
      }
      // Force an absolute https URL — Shopify's image_url filter emits a
      // protocol-relative "//cdn..." which some email clients won't load.
      var img = (lv && lv.image) ? lv.image : '';
      if (img.indexOf('//') === 0) img = 'https:' + img;
      return {
        id: (lv && lv.id != null) ? String(lv.id) : '',
        variant: '',
        rank: i + 1,
        image: img,
        title: (lv && lv.title) ? lv.title : PRODUCTS[r.key].name,
        url: (lv && lv.url) ? lv.url : '',
        score: Math.max(r.pct, i === 0 ? 60 : 55),
        price: { amount: amount, currency: 'GBP' }
      };
    });

    // answers[] — one entry per answered question, in Lantern's shape.
    var ans = [];
    if (a.impact) ans.push({ answersArray: [L_IMPACT[a.impact] || a.impact], answer: (L_IMPACT[a.impact] || a.impact), question: Q_TEXT.impact, index: 1, answerType: 'single' });
    if (a.size)   ans.push({ answersArray: [L_SIZE[a.size] || a.size],       answer: (L_SIZE[a.size] || a.size),       question: Q_TEXT.size,   index: 2, answerType: 'single' });
    if (a.strap)  ans.push({ answersArray: [L_STRAP[a.strap] || a.strap],    answer: (L_STRAP[a.strap] || a.strap),    question: Q_TEXT.strap,  index: 3, answerType: 'single' });
    if (a.features && a.features.length) {
      var fl = a.features.map(function (f) { return L_FEAT[f] || f; });
      ans.push({ answersArray: fl, answer: fl.join(', '), question: Q_TEXT.features, index: 4, answerType: 'multiple' });
    }

    var sid = uuid();
    var origin = (window.location && window.location.origin) ? window.location.origin : 'https://www.maaree.com';

    return {
      products: products,
      answers: ans,
      session_id: sid,
      results_url_short: '/sports-bra-quiz#/results/' + sid,
      results_url_full: origin + '/pages/sports-bra-quiz#/results/' + sid,
      winner_scores: [products[0] ? products[0].score : 0],
      // Convenience fields for segmentation / simpler email templating.
      match_bra: map.live ? map.live.title : map.name,
      match_style: map.name,
      match_pct: computeMatch(a)
    };
  }

  /* ---- Klaviyo hand-off ----
     Mirrors what the old Lantern quiz did, but from the browser using
     Klaviyo's Client APIs (authenticated by the PUBLIC key / company_id
     only — no private key ever touches the page). Three calls:

       1. POST /client/profiles/      → create/update the profile with the
                                         shopper's name, email + light
                                         segmentation properties.
       2. POST /client/events/        → fire the "Completed Match Quiz"
                                         event carrying the full result
                                         payload (answers + ranked product
                                         recommendations). THIS is the step
                                         the previous code never did, which
                                         is why no quiz data ever reached
                                         Klaviyo and the metric never existed.
       3. POST /client/subscriptions/ → opt the profile into the quiz list
                                         (consent). Skipped if no list is set;
                                         the event in step 2 still fires.

     All three are CORS-enabled client endpoints, safe to call from a theme.
     Failures are logged to the console and never block the UX. */
  function klaviyoSubscribe(email, firstName, ctx) {
    if (!email) return Promise.resolve();
    ctx = ctx || {};

    // Best-effort onsite identify: links the Klaviyo browser cookie to this
    // profile for on-site personalisation. We deliberately do NOT call
    // onsite track() here — the Client Events call below is the single
    // source of truth for the event, so it can't be double-counted.
    try {
      var k = window.klaviyo;
      if (k && typeof k.identify === 'function') {
        k.identify({ email: email, first_name: firstName || '' });
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['identify', { '$email': email, '$first_name': firstName || '' }]);
      }
    } catch (e) { /* never block the UX */ }

    var key = cfg.klaviyoKey;
    if (!key) return Promise.resolve();   // public key required for all client calls
    var REV = '2024-10-15';
    var CLIENT = 'https://a.klaviyo.com/client/';

    function post(path, body) {
      return fetch(CLIENT + path + '?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': REV },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) {
          try { r.text().then(function (t) { console.warn('[style-quiz] Klaviyo ' + path + ' failed', r.status, t); }); }
          catch (e) {}
        }
        return r;
      }).catch(function () { /* never block the UX */ });
    }

    // 1. Profile — name, email + properties.
    //    CRITICAL: the live "Quiz Template" email reads PROFILE properties
    //    (not the event) for the three recommended products — exactly the
    //    ones the old Lantern app wrote: "ProductName (1..3)",
    //    "ImageUrl (1..3)", "Score (1..3)" and an "AllProducts" array used
    //    for price. We write the SAME keys here so the existing email renders
    //    this quiz's result with zero changes in Klaviyo.
    var profileProps = {
      last_quiz: 'Sports Bra Quiz',
      last_quiz_match_bra: ctx.match_bra || '',
      last_quiz_match_style: ctx.match_style || '',
      last_quiz_match_pct: ctx.match_pct || 0,
      last_quiz_date: new Date().toISOString()
    };
    var prods = ctx.products || [];
    var allProducts = [];
    for (var i = 0; i < prods.length && i < 3; i++) {
      var n = i + 1, p = prods[i];
      profileProps['ProductName (' + n + ')'] = p.title || '';
      profileProps['ImageUrl (' + n + ')']    = p.image || '';
      profileProps['Score (' + n + ')']       = (p.score != null ? p.score : '');
      // Email prints "{{ ...maxVariantPrice.amount }}0", so store a one-decimal
      // amount (e.g. "72.0") and the trailing 0 makes it "72.00".
      var amt = (p.price && p.price.amount != null && p.price.amount !== '' && !isNaN(Number(p.price.amount)))
        ? Number(p.price.amount).toFixed(1) : '';
      allProducts.push({ product: { priceRangeV2: { maxVariantPrice: {
        amount: amt, currencyCode: (p.price && p.price.currency) || 'GBP'
      } } } });
    }
    profileProps.AllProducts = allProducts;

    // Write the profile FIRST and wait for it to be accepted, so the
    // recommended-product properties exist before the event below triggers
    // the flow + email (the email reads them from the profile).
    return post('profiles/', {
      data: {
        type: 'profile',
        attributes: {
          email: email,
          first_name: firstName || '',
          properties: profileProps
        }
      }
    }).then(function () {

      // 2. Event — the completed-quiz record with the full result payload.
      //    The metric name MUST match what the live "Bra Quiz Participants"
      //    flow (R4wjCc) triggers on, so the new quiz drops straight into the
      //    existing flow and its "Your Sports Bra Quiz Results" email.
      //    (Confirmed in Klaviyo 2026-06-05 from the flow's trigger card.)
      post('events/', {
        data: {
          type: 'event',
          attributes: {
            properties: ctx,
            metric:  { data: { type: 'metric',  attributes: { name: 'Completed Lantern Quiz (v2) - sports-bra-quiz' } } },
            profile: { data: { type: 'profile', attributes: { email: email, first_name: firstName || '' } } }
          }
        }
      });

      // 3. List subscription (consent). Optional — only if a list is configured.
      if (cfg.klaviyoList) {
        post('subscriptions/', {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Sports Bra Quiz',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    first_name: firstName || '',
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: cfg.klaviyoList } } }
          }
        });
      }
    });
  }

  /* ---- Fit Finder drawer (lazy-load if needed) ---- */
  function openFitFinder() {
    if (window.MaareeFitFinder && window.MaareeFitFinder.openDrawer) {
      window.MaareeFitFinder.openDrawer({});
      return;
    }
    if (!cfg.ffJs) {
      if (cfg.sizeGuideUrl) window.location.href = cfg.sizeGuideUrl;
      return;
    }
    if (cfg.ffCss && !document.querySelector('link[href="' + cfg.ffCss + '"]')) {
      var css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = cfg.ffCss;
      document.head.appendChild(css);
    }
    function loadScript(src) {
      return new Promise(function (res, rej) {
        var s = document.createElement('script');
        s.src = src; s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
    }
    var chain = cfg.ffSizing ? loadScript(cfg.ffSizing) : Promise.resolve();
    chain.then(function () { return loadScript(cfg.ffJs); })
      .then(function () { if (window.MaareeFitFinder) window.MaareeFitFinder.openDrawer({}); })
      .catch(function () { if (cfg.sizeGuideUrl) window.location.href = cfg.sizeGuideUrl; });
  }

  /* ---- Live match meter update on the question screens ---- */
  function updateLiveMeter() {
    var fill = stage.querySelector('.q-livematch__fill');
    var pctEl = stage.querySelector('.q-livematch__pct');
    if (!fill || !pctEl) return;
    var answered = ['impact', 'size', 'strap'].filter(function (k) { return answers[k]; }).length
      + (answers.features.length ? 1 : 0);
    var pct = Math.round((answered / 4) * 100);
    fill.style.width = pct + '%';
    pctEl.textContent = pct + '%';
  }

  /* ---- Helpers ---- */
  function setText(sel, val) { var el = stage.querySelector(sel); if (el) el.textContent = val; }

  /* ---- Bind events on the freshly-rendered screen ---- */
  function bind() {
    stage.querySelectorAll('[data-action="start"], [data-action="next"]').forEach(function (b) {
      b.addEventListener('click', function () { idx++; render(); });
    });
    stage.querySelectorAll('[data-action="prev"]').forEach(function (b) {
      b.addEventListener('click', function () { idx = Math.max(0, idx - 1); render(); });
    });
    stage.querySelectorAll('[data-action="restart"]').forEach(function (b) {
      b.addEventListener('click', function () {
        idx = 0;
        answers = { impact: null, size: null, strap: null, features: [], name: '', email: '' };
        render();
      });
    });

    stage.querySelectorAll('[data-q="impact"], [data-q="size"], [data-q="strap"]').forEach(function (b) {
      b.addEventListener('click', function () {
        var q = b.dataset.q;
        answers[q] = b.dataset.value;
        stage.querySelectorAll('[data-q="' + q + '"]').forEach(function (s) { s.classList.remove('is-selected'); });
        b.classList.add('is-selected');
        var next = stage.querySelector('[data-action="next"]');
        if (next) next.disabled = false;
      });
    });

    stage.querySelectorAll('[data-q="features"]').forEach(function (b) {
      b.addEventListener('click', function () {
        var v = b.dataset.value;
        if (v === 'any') {
          answers.features = ['any'];
          stage.querySelectorAll('[data-q="features"]').forEach(function (s) { s.classList.remove('is-selected'); });
          b.classList.add('is-selected');
        } else {
          var i = answers.features.indexOf(v);
          if (i > -1) answers.features.splice(i, 1); else answers.features.push(v);
          var anyI = answers.features.indexOf('any');
          if (anyI > -1) answers.features.splice(anyI, 1);
          b.classList.toggle('is-selected');
          var anyBtn = stage.querySelector('[data-q="features"][data-value="any"]');
          if (anyBtn) anyBtn.classList.remove('is-selected');
        }
      });
    });

    var gate = stage.querySelector('#mq-gate-form');
    if (gate) {
      gate.addEventListener('submit', function (e) {
        e.preventDefault();
        answers.name = (stage.querySelector('#mq-g-name') || {}).value || '';
        answers.email = (stage.querySelector('#mq-g-email') || {}).value || '';
        var map = mapResult(answers);
        klaviyoSubscribe(answers.email, answers.name, buildLanternPayload(answers, map));
        var preview = stage.querySelector('#mq-preview');
        if (preview) preview.classList.add('is-revealed');
        setTimeout(function () { idx++; render(); }, 900);
      });
    }

    var ffBtn = stage.querySelector('[data-action="fitfinder"]');
    if (ffBtn) ffBtn.addEventListener('click', function (e) { e.preventDefault(); openFitFinder(); });

    updateLiveMeter();
  }

  render();
})();